import { MDM_CAPABILITY } from '@/lib/constants/mdm/constants'
import { isCapabilityOpen } from '@/components/paywall/paywallUtils'
import { CONTEXT_DOCUMENTS_ROUTE } from '@/lib/constants/contextDocuments'
import { PERMISSIONS } from '@/lib/constants/permissions'
import { SUPPLIER_QUESTIONNAIRES } from '@/lib/constants/supplierQuestionnaires'
import { USER_ROLES } from '@/lib/constants/users'
import type { IPermission } from '@/types/permissions'
import type { ISession } from '@/types/session'
import type { IUserForPermissions, IUserRole } from '@/types/user'

const EDITOR_PERMISSIONS: IPermission[] = [
  PERMISSIONS.VIEW_HOME,
  PERMISSIONS.VIEW_COMPLIANCE,
  PERMISSIONS.VIEW_TASKS,
  PERMISSIONS.VIEW_PEOPLE,
  PERMISSIONS.VIEW_PASSWORD_MANAGEMENT,
  PERMISSIONS.VIEW_PHISHING_EXERCISES,
  PERMISSIONS.VIEW_EMPLOYEES_TRAINING,
  PERMISSIONS.VIEW_EDR_SIEM,
  PERMISSIONS.MANAGE_EDR,
  PERMISSIONS.VIEW_AUTOMATED_PENTEST,
  PERMISSIONS.VIEW_ASSET_MANAGEMENT,
  PERMISSIONS.VIEW_CODE_SCANNER,
  PERMISSIONS.MANAGE_USERS,
  PERMISSIONS.VIEW_INFRASTRUCTURE,
  PERMISSIONS.VIEW_BILLING,
  PERMISSIONS.LOAD_DICTIONARIES,
  PERMISSIONS.VIEW_DOCUMENTS,
  PERMISSIONS.VIEW_APPROVED_TEXT_DOCUMENTS,
  PERMISSIONS.VIEW_OPEN_SEARCH,
  PERMISSIONS.MANAGE_COMPLIANCE,
  PERMISSIONS.MANAGE_CONNECTORS,
  PERMISSIONS.MANAGE_CASES,
  PERMISSIONS.VIEW_APPSEC,
  PERMISSIONS.VIEW_EASM,
  PERMISSIONS.VIEW_SUPPLIERS,
  PERMISSIONS.VIEW_CLOUD_SECURITY,
  PERMISSIONS.VIEW_EMPLOYEES_CENTER,
  PERMISSIONS.VIEW_EMPLOYEE_DASHBOARD,
  PERMISSIONS.VIEW_CONNECTORS,
  PERMISSIONS.VIEW_IDENTITIES,
  PERMISSIONS.VIEW_ASSETS,
  PERMISSIONS.VIEW_REPORTS,
  PERMISSIONS.SIGN_POLICIES,
  PERMISSIONS.VIEW_SUPPLIER_QUESTIONNAIRES,
  PERMISSIONS.MANAGE_SUPPLIER_QUESTIONNAIRES,
]

export const ROLE_PERMISSIONS: Record<IUserRole, IPermission[]> = {
  [USER_ROLES.ADMIN]: Object.values(PERMISSIONS), // ALL THE PERMISSIONS
  // Not in EDITOR_PERMISSIONS: MDM_EDITOR spreads that array and must stay read-only here.
  [USER_ROLES.EDITOR]: [
    ...EDITOR_PERMISSIONS,
    PERMISSIONS.MANAGE_CLOUD_SECURITY,
    PERMISSIONS.MANAGE_APPSEC,
    PERMISSIONS.MANAGE_EASM,
    PERMISSIONS.MANAGE_INFRASTRUCTURE,
    PERMISSIONS.MANAGE_API_KEYS,
  ],
  [USER_ROLES.VIEWER]: [
    PERMISSIONS.VIEW_EMPLOYEES_CENTER,
    PERMISSIONS.SIGN_POLICIES,
    PERMISSIONS.VIEW_APPROVED_TEXT_DOCUMENTS,
  ],
  [USER_ROLES.SUPPLIER]: [
    PERMISSIONS.VIEW_EMPLOYEES_CENTER,
    PERMISSIONS.SIGN_POLICIES,
    PERMISSIONS.VIEW_APPROVED_TEXT_DOCUMENTS,
    PERMISSIONS.VIEW_SUPPLIER_QUESTIONNAIRES,
    PERMISSIONS.MANAGE_SUPPLIER_QUESTIONNAIRES,
  ],
  [USER_ROLES.CONSULTANT]: [
    PERMISSIONS.VIEW_HOME,
    PERMISSIONS.VIEW_COMPLIANCE,
    PERMISSIONS.VIEW_TASKS,
    PERMISSIONS.VIEW_EMPLOYEE_DASHBOARD,
    PERMISSIONS.VIEW_PEOPLE,
    PERMISSIONS.BULK_CREATION_ACTIONS,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_TENANTS,
    PERMISSIONS.MANAGE_COMPLIANCE,
    PERMISSIONS.MANAGE_CONNECTORS,
    PERMISSIONS.LOAD_DICTIONARIES,
    PERMISSIONS.VIEW_DOCUMENTS,
    PERMISSIONS.VIEW_APPROVED_TEXT_DOCUMENTS,
    PERMISSIONS.VIEW_OPEN_SEARCH,
    PERMISSIONS.VIEW_CONNECTORS,
    PERMISSIONS.VIEW_IDENTITIES,
  ],
  [USER_ROLES.CONSULTANT_ADMIN]: [
    PERMISSIONS.MANAGE_CONNECTORS,
    PERMISSIONS.MANAGE_CONSULTANTS,
    PERMISSIONS.MANAGE_TENANTS,
    PERMISSIONS.VIEW_ALL_OWN_TENANTS,
    PERMISSIONS.VIEW_TENANTS,
    PERMISSIONS.VIEW_HOME,
    PERMISSIONS.VIEW_COMPLIANCE,
    PERMISSIONS.VIEW_TASKS,
    PERMISSIONS.VIEW_BACKOFFICE,
    PERMISSIONS.VIEW_EMPLOYEE_DASHBOARD,
    PERMISSIONS.VIEW_PEOPLE,
    PERMISSIONS.BULK_CREATION_ACTIONS,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.LOAD_DICTIONARIES,
    PERMISSIONS.VIEW_DOCUMENTS,
    PERMISSIONS.VIEW_APPROVED_TEXT_DOCUMENTS,
    PERMISSIONS.VIEW_OPEN_SEARCH,
    PERMISSIONS.VIEW_CONNECTORS,
    PERMISSIONS.VIEW_IDENTITIES,
  ],
  [USER_ROLES.AUDITOR]: [
    PERMISSIONS.VIEW_HOME,
    PERMISSIONS.VIEW_COMPLIANCE,
    PERMISSIONS.VIEW_PEOPLE,
    PERMISSIONS.VIEW_EMPLOYEE_DASHBOARD,
    PERMISSIONS.VIEW_EMPLOYEES_CENTER,
    PERMISSIONS.VIEW_EMPLOYEES_TRAINING,
    PERMISSIONS.VIEW_PHISHING_EXERCISES,
    PERMISSIONS.VIEW_PASSWORD_MANAGEMENT,
    PERMISSIONS.VIEW_DOCUMENTS,
    PERMISSIONS.VIEW_APPROVED_TEXT_DOCUMENTS,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.VIEW_ASSETS,
    PERMISSIONS.VIEW_ASSET_MANAGEMENT,
    PERMISSIONS.VIEW_SUPPLIERS,
    PERMISSIONS.VIEW_INFRASTRUCTURE,
    PERMISSIONS.VIEW_EASM,
    PERMISSIONS.VIEW_APPSEC,
    PERMISSIONS.VIEW_CLOUD_SECURITY,
    PERMISSIONS.VIEW_CONNECTORS,
    PERMISSIONS.VIEW_IDENTITIES,
    PERMISSIONS.VIEW_EDR_SIEM,
    PERMISSIONS.VIEW_AUTOMATED_PENTEST,
    PERMISSIONS.VIEW_CODE_SCANNER,
    PERMISSIONS.VIEW_OPEN_SEARCH,
    PERMISSIONS.VIEW_CRISIS_MANAGER,
    PERMISSIONS.VIEW_MDM,
    PERMISSIONS.VIEW_TRUST_CENTER,
    PERMISSIONS.VIEW_SUPPLIER_QUESTIONNAIRES,
  ],
  [USER_ROLES.MDM_EDITOR]: [...EDITOR_PERMISSIONS, PERMISSIONS.VIEW_MDM, PERMISSIONS.MANAGE_MDM],
}

export const PROTECTED_ROUTES = [
  { path: '/', requiredPermissions: [PERMISSIONS.VIEW_HOME] },
  { path: '/assets', requiredPermissions: [PERMISSIONS.VIEW_ASSETS] },
  { path: '/assets/laptops', requiredPermissions: [PERMISSIONS.VIEW_ASSETS] },
  { path: '/assets/servers', requiredPermissions: [PERMISSIONS.VIEW_ASSETS] },
  { path: '/suppliers', requiredPermissions: [PERMISSIONS.VIEW_SUPPLIERS] },
  { path: '/suppliers/dashboard', requiredPermissions: [PERMISSIONS.VIEW_SUPPLIERS] },
  { path: '/suppliers/questionnaires', requiredPermissions: [PERMISSIONS.VIEW_SUPPLIER_QUESTIONNAIRES] },
  { path: CONTEXT_DOCUMENTS_ROUTE, requiredPermissions: [PERMISSIONS.VIEW_SUPPLIER_QUESTIONNAIRES] },
  { path: '/compliance', requiredPermissions: [PERMISSIONS.VIEW_COMPLIANCE] },
  { path: '/compliance/controls', requiredPermissions: [PERMISSIONS.VIEW_COMPLIANCE] },
  { path: '/compliance/documents', requiredPermissions: [PERMISSIONS.VIEW_COMPLIANCE] },
  { path: '/compliance/evidences', requiredPermissions: [PERMISSIONS.VIEW_COMPLIANCE] },
  { path: '/compliance/tasks', requiredPermissions: [PERMISSIONS.VIEW_COMPLIANCE, PERMISSIONS.VIEW_TASKS] },
  { path: '/compliance/non-conformities', requiredPermissions: [PERMISSIONS.VIEW_COMPLIANCE] },
  { path: '/compliance/risk-manager', requiredPermissions: [PERMISSIONS.VIEW_COMPLIANCE] },
  { path: '/people', requiredPermissions: [PERMISSIONS.VIEW_PEOPLE] },
  { path: '/people/employee-dashboard', requiredPermissions: [PERMISSIONS.VIEW_PEOPLE] },
  { path: '/people/saas-management', requiredPermissions: [PERMISSIONS.VIEW_PEOPLE] },
  { path: '/people/password-manager', requiredPermissions: [PERMISSIONS.VIEW_PASSWORD_MANAGEMENT] },
  { path: '/people/phishing-campaigns', requiredPermissions: [PERMISSIONS.VIEW_PHISHING_EXERCISES] },
  { path: '/people/employees-training', requiredPermissions: [PERMISSIONS.VIEW_EMPLOYEES_TRAINING] },
  { path: '/people/endpoints-compliance', requiredPermissions: [PERMISSIONS.VIEW_PEOPLE] },
  { path: '/people/identities', requiredPermissions: [PERMISSIONS.VIEW_IDENTITIES] },
  { path: '/identities', requiredPermissions: [PERMISSIONS.VIEW_IDENTITIES] },
  { path: '/cloud-security', requiredPermissions: [PERMISSIONS.VIEW_CLOUD_SECURITY] },
  { path: '/cloud-security/dashboard', requiredPermissions: [PERMISSIONS.VIEW_CLOUD_SECURITY] },
  { path: '/cloud-security/misconfigurations', requiredPermissions: [PERMISSIONS.VIEW_CLOUD_SECURITY] },
  { path: '/infrastructure', requiredPermissions: [PERMISSIONS.VIEW_INFRASTRUCTURE] },
  { path: '/infrastructure/dashboard', requiredPermissions: [PERMISSIONS.VIEW_INFRASTRUCTURE] },
  { path: '/infrastructure/alerts', requiredPermissions: [PERMISSIONS.VIEW_INFRASTRUCTURE] },
  { path: '/infrastructure/endpoint-monitoring', requiredPermissions: [PERMISSIONS.VIEW_INFRASTRUCTURE] },
  { path: '/infrastructure/vulnerabilities', requiredPermissions: [PERMISSIONS.VIEW_INFRASTRUCTURE] },
  { path: '/easm', requiredPermissions: [PERMISSIONS.VIEW_EASM] },
  { path: '/easm/dashboard', requiredPermissions: [PERMISSIONS.VIEW_EASM] },
  { path: '/easm/public-domains', requiredPermissions: [PERMISSIONS.VIEW_EASM] },
  { path: '/easm/vulnerabilities', requiredPermissions: [PERMISSIONS.VIEW_EASM] },
  { path: '/easm/certificates', requiredPermissions: [PERMISSIONS.VIEW_EASM] },
  { path: '/easm/breaches', requiredPermissions: [PERMISSIONS.VIEW_EASM] },
  { path: '/billing', requiredPermissions: [PERMISSIONS.VIEW_BILLING] },
  { path: '/backoffice', requiredPermissions: [PERMISSIONS.VIEW_BACKOFFICE, PERMISSIONS.MANAGE_CONSULTANTS] },
  { path: '/backoffice/customer-success', requiredPermissions: [PERMISSIONS.VIEW_BACKOFFICE] },
  { path: '/backoffice/compliance-data', requiredPermissions: [PERMISSIONS.VIEW_BACKOFFICE] },
  { path: SUPPLIER_QUESTIONNAIRES.BACKOFFICE_PATH, requiredPermissions: [PERMISSIONS.VIEW_BACKOFFICE] },
  { path: '/backoffice/chat-logs', requiredPermissions: [PERMISSIONS.VIEW_CHAT_LOGS] },
  { path: '/employee-center', requiredPermissions: [PERMISSIONS.VIEW_EMPLOYEES_CENTER] },
  { path: '/employees-center/password-vault', requiredPermissions: [PERMISSIONS.VIEW_EMPLOYEES_CENTER] },
  { path: '/employees-center/endpoint-protection', requiredPermissions: [PERMISSIONS.VIEW_EMPLOYEES_CENTER] },
  { path: '/employees-center/awareness-training', requiredPermissions: [PERMISSIONS.VIEW_EMPLOYEES_CENTER] },
  { path: '/appsec', requiredPermissions: [PERMISSIONS.VIEW_APPSEC] },
  { path: '/appsec/dashboard', requiredPermissions: [PERMISSIONS.VIEW_APPSEC] },
  { path: '/appsec/sast', requiredPermissions: [PERMISSIONS.VIEW_APPSEC] },
  { path: '/appsec/sca', requiredPermissions: [PERMISSIONS.VIEW_APPSEC] },
  { path: '/appsec/secrets-detection', requiredPermissions: [PERMISSIONS.VIEW_APPSEC] },
  { path: '/appsec/sbom', requiredPermissions: [PERMISSIONS.VIEW_APPSEC] },
  { path: '/appsec/containers-scan', requiredPermissions: [PERMISSIONS.VIEW_APPSEC] },
  { path: '/appsec/dast', requiredPermissions: [PERMISSIONS.VIEW_APPSEC] },
  { path: '/connectors', requiredPermissions: [PERMISSIONS.VIEW_CONNECTORS] },
  { path: '/crisis-manager', requiredPermissions: [PERMISSIONS.VIEW_CRISIS_MANAGER] },
  { path: '/mdm/dashboard', requiredPermissions: [PERMISSIONS.VIEW_MDM] },
  { path: '/mdm/groups', requiredPermissions: [PERMISSIONS.VIEW_MDM] },
  { path: '/mdm/software', requiredPermissions: [PERMISSIONS.VIEW_MDM] },
  { path: '/mdm/policies', requiredPermissions: [PERMISSIONS.VIEW_MDM] },
  { path: '/mdm/scripts', requiredPermissions: [PERMISSIONS.VIEW_MDM] },
]

export const hasPermission = ({
  user,
  permission,
}: {
  user: IUserForPermissions
  permission: IPermission
}): boolean => {
  return ROLE_PERMISSIONS[user.role].includes(permission)
}

export function isRouteAuthorized({ user, path }: { user: IUserForPermissions; path: string }): boolean {
  const route = PROTECTED_ROUTES.find((r) => r.path === path)
  if (!route) return false
  return route.requiredPermissions.every((perm) => hasPermission({ user, permission: perm }))
}

export function isAdmin(user: IUserForPermissions): boolean {
  return user.role === USER_ROLES.ADMIN
}

export function canUserManageMDM(session: ISession): boolean {
  const organization = session.user.simulatedOrganization || session.user.organization
  const hasMdmCapability = isCapabilityOpen(organization, MDM_CAPABILITY)
  return hasPermission({ user: session.user, permission: PERMISSIONS.MANAGE_MDM }) && hasMdmCapability
}

export function canUserViewMDM(session: ISession): boolean {
  const organization = session.user.simulatedOrganization || session.user.organization
  const hasMdmCapability = isCapabilityOpen(organization, MDM_CAPABILITY)
  return hasPermission({ user: session.user, permission: PERMISSIONS.VIEW_MDM }) && hasMdmCapability
}
